// Must be the very first import to silence console before any module logs
import '@/utils/consoleSilencer';

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';
import { PersistGate } from 'redux-persist/integration/react';

import { store, persistor } from '@/store';
import { queryClient, setupQueryPersistence, setupMedicalDataSync, setupNetworkDetection } from '@/services/queryClient';
import App from '@/App';
import '@/index.css';
import '@/i18n'; // Initialize i18n

// ============================================================
// STARTUP DEBUG - AI Image Generation System Check
// ============================================================
const checkAISystemConfig = () => {
  console.log('🤖 ========================================');
  console.log('🤖 AI IMAGE GENERATION SYSTEM STATUS');
  console.log('🤖 ========================================');
  console.log('🤖 ✅ API calls proxied via backend (key segura en servidor)');
  console.log('🤖 Environment:', import.meta.env.VITE_ENVIRONMENT || 'unknown');
  console.log('🤖 Debug mode:', import.meta.env.VITE_ENABLE_DEBUG || 'false');
  console.log('🤖 ========================================');
};

// Run AI system check on startup
checkAISystemConfig();

// Reload once when lazy-loaded chunks 404 after a frontend deploy (stale browser/SW cache)
const CHUNK_RELOAD_KEY = 'gynhub-chunk-reload';

const isStaleChunkLoadError = (reason: unknown): boolean => {
  const message =
    reason instanceof Error
      ? reason.message
      : typeof reason === 'string'
        ? reason
        : '';
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes('error loading dynamically imported module')
  );
};

const reloadOnceForStaleChunks = (): void => {
  if (sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
    return;
  }
  sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
  window.location.reload();
};

window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  reloadOnceForStaleChunks();
});

window.addEventListener('unhandledrejection', (event) => {
  if (isStaleChunkLoadError(event.reason)) {
    event.preventDefault();
    reloadOnceForStaleChunks();
  }
});

window.addEventListener('load', () => {
  sessionStorage.removeItem(CHUNK_RELOAD_KEY);
});

// Initialize medical application setup only once
let isMedicalSystemInitialized = false;

const initializeMedicalSystem = async () => {
  if (isMedicalSystemInitialized) return;
  
  try {
    setupQueryPersistence();
    setupNetworkDetection();
    
    // Initialize enhanced offline support
    await setupMedicalDataSync();
    
    isMedicalSystemInitialized = true;
    console.log('Medical system initialization completed');
  } catch (error) {
    console.error('Medical system initialization failed:', error);
  }
};

// Start initialization only once
if (!isMedicalSystemInitialized) {
  initializeMedicalSystem();
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <HelmetProvider>
      <Provider store={store}>
        <PersistGate loading={<div>Loading medical system...</div>} persistor={persistor}>
          <QueryClientProvider client={queryClient}>
            <BrowserRouter>
              <App />
              <Toaster
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: '#363636',
                    color: '#fff',
                  },
                  success: {
                    style: {
                      background: '#10b981',
                      color: '#fff',
                    },
                  },
                  error: {
                    style: {
                      background: '#ef4444',
                      color: '#fff',
                    },
                  },
                }}
              />
              {/* Show React Query DevTools only in development */}
              {process.env.NODE_ENV === 'development' && (
                <ReactQueryDevtools
                  initialIsOpen={false}
                />
              )}
            </BrowserRouter>
          </QueryClientProvider>
        </PersistGate>
      </Provider>
    </HelmetProvider>
  </React.StrictMode>
);
